import {
  ActionBarPrimitive,
  AuiIf,
  ErrorPrimitive,
  MessagePrimitive,
  useAuiState,
  useMessagePartText,
} from '@assistant-ui/react';
import {
  Button,
  DotProgress,
  Icon,
  IconButton,
  TextField,
  ToggleButton,
} from '@wandb/components';
import copyToClipboard from 'copy-to-clipboard';
import React, {type FC, useCallback, useEffect, useState} from 'react';

import {extractTurnId} from '../../agentChat/runtime/turn';
import {useSubmitAgentFeedback} from '../../agentChat/useSubmitAgentFeedback';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {decodePromptForMessageDisplay} from './utils';
import {MarkdownText, WBAgentMarkdown} from './WBAgentMarkdown';
import {WBAgentReasoning} from './WBAgentReasoning';
import {WBAgentToolCall} from './WBAgentToolCall';
import {WBAgentToolGroup} from './WBAgentToolGroup';

const messagePartComponents: NonNullable<
  React.ComponentProps<typeof MessagePrimitive.Parts>['components']
> = {
  Reasoning: WBAgentReasoning,
  Text: MarkdownText,
  ToolGroup: WBAgentToolGroup,
  tools: {
    Fallback: WBAgentToolCall,
  },
};

export const AssistantMessage: FC = () => {
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isFading, setIsFading] = useState<boolean>(false);

  useEffect(() => {
    if (!isSubmitted) {
      return;
    }
    const fadeTimer = setTimeout(() => setIsFading(true), 1000);
    const removeTimer = setTimeout(() => {
      setIsSubmitted(false);
      setIsFading(false);
    }, 1500);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [isSubmitted]);

  const submitFeedback = useSubmitAgentFeedback();
  const messageId = useAuiState(s => s.message.id);
  const turnId = extractTurnId(messageId);

  const handleDismissFeedbackForm = (feedbackReasoning?: string) => {
    setIsFeedbackFormOpen(false);
    if (turnId) {
      submitFeedback(turnId, false, feedbackReasoning);
    }
  };

  return (
    <MessagePrimitive.Root
      className="mx-auto w-full max-w-(--thread-max-width) pt-16"
      data-role="assistant">
      <div className="flex flex-col gap-16 break-words px-2 text-moon-650 text-[15px] leading-[21px]">
        <MessagePrimitive.Parts components={messagePartComponents} />
        <AuiIf
          condition={s =>
            s.thread.isRunning &&
            s.message.isLast &&
            !s.message.parts.some(
              p => p.type === 'tool-call' && p.status?.type === 'running'
            )
          }>
          <div className="mb-8 flex flex-col items-start self-stretch text-[#C5C7CC]">
            <DotProgress aria-label="agent thinking" />
          </div>
        </AuiIf>
      </div>
      <MessagePrimitive.Error>
        <ErrorPrimitive.Root className="mt-8 flex items-center gap-8 rounded-lg border border-red-300 bg-red-50 px-12 py-8 text-[15px] text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <span className="inline-flex shrink-0">
            <Icon icon="warning" color="red" />
          </span>
          <ErrorPrimitive.Message className="flex-1" />
          <CopyErrorButton />
        </ErrorPrimitive.Root>
      </MessagePrimitive.Error>
      <AuiIf condition={s => !(s.thread.isRunning && s.message.isLast)}>
        <div className="flex justify-between items-center pb-16">
          <CopyMessageButton />
          <FeedbackBar
            turnId={turnId}
            submitFeedback={submitFeedback}
            onFeedbackOpenChange={setIsFeedbackFormOpen}
          />
        </div>
      </AuiIf>
      {isFeedbackFormOpen && (
        <FeedbackReasoningForm
          onDismiss={handleDismissFeedbackForm}
          onSubmit={setIsSubmitted}
        />
      )}
      {isSubmitted && (
        <div
          className={`flex items-center gap-7 text-moon-800 font-normal transition-opacity duration-500 ${
            isFading ? 'opacity-0' : 'opacity-100'
          }`}>
          <Icon icon="checkmark" color="teal" /> Thanks for the feedback!
        </div>
      )}
    </MessagePrimitive.Root>
  );
};

/**
 * CopyMessageButton is a copy button that hides entirely when there is no text
 * content to copy. AUI's `ActionBarPrimitive.Copy` only *disables* the button;
 * this component will be hidden entirely.
 */
const CopyMessageButton: FC = () => {
  const hasText = useAuiState(s =>
    s.message.parts.some(
      (c: {type: string; text?: string}) =>
        c.type === 'text' && (c.text?.length ?? 0) > 0
    )
  );

  if (!hasText) {
    return null;
  }

  return (
    <ActionBarPrimitive.Copy asChild>
      <IconButton variant="ghost" icon="copy" aria-label="copy" />
    </ActionBarPrimitive.Copy>
  );
};

/**
 * CopyErrorButton is a copy button placed within error messages, allowing the
 * user to copy *just* the error message, as opposed to message-level copy
 * buttons which may copy partial text responses above an error.
 */
const CopyErrorButton: FC = () => {
  const error = useAuiState(s =>
    s.message.status?.type === 'incomplete' &&
    s.message.status.reason === 'error'
      ? s.message.status.error
      : undefined
  );

  const handleCopy = useCallback(() => {
    if (error != null) {
      copyToClipboard(String(error));
    }
  }, [error]);

  if (error == null) {
    return null;
  }

  return (
    <IconButton
      variant="ghost"
      size="sm"
      icon="copy"
      aria-label="copy error"
      onClick={handleCopy}
    />
  );
};

const FeedbackBar = ({
  turnId,
  submitFeedback,
  onFeedbackOpenChange,
}: {
  turnId: string | null;
  submitFeedback: (
    turnId: string,
    feedbackIsPositive: boolean | null,
    feedbackReasoning?: string | null
  ) => void;
  onFeedbackOpenChange: (isOpen: boolean) => void;
}) => {
  const [sentiment, setSentiment] = useState<'positive' | 'negative' | null>(
    null
  );

  const handleThumb = (value: 'positive' | 'negative') => {
    const newSentiment = sentiment === value ? null : value;
    setSentiment(newSentiment);
    onFeedbackOpenChange(newSentiment === 'negative');

    // Send positive feedback or retract pos/neg feedback
    if (newSentiment !== 'negative' && turnId) {
      submitFeedback(turnId, newSentiment === 'positive' ? true : null);
    }
  };

  return (
    <div className="ml-auto">
      <IconButton
        aria-label="positive feedback"
        icon="thumbs-up"
        onClick={() => handleThumb('positive')}
        variant={sentiment === 'positive' ? 'primary' : 'ghost'}
      />
      <IconButton
        aria-label="negative feedback"
        icon="thumbs-down"
        onClick={() => handleThumb('negative')}
        variant={sentiment === 'negative' ? 'primary' : 'ghost'}
      />
    </div>
  );
};

const FeedbackReasoningForm = ({
  onDismiss,
  onSubmit,
}: {
  onDismiss: (feedbackReasoning?: string) => void;
  onSubmit: (isSubmitted: boolean) => void;
}) => {
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(
    new Set()
  );
  const nightModeRevertFilter = useNightModeRevertFilter();
  const [details, setDetails] = useState('');
  const suggestions = ['Inaccurate', 'Not relevant', 'Too vague'];

  const handleToggleSuggestion = (suggestion: string, isSelected: boolean) => {
    setSelectedSuggestions(prev => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(suggestion);
      } else {
        next.delete(suggestion);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    const parts = [...selectedSuggestions];
    if (details.trim()) {
      parts.push(details.trim());
    }
    onDismiss(parts.length > 0 ? parts.join(', ') : undefined);
    onSubmit(true);
  };

  return (
    <div
      className="relative flex flex-col gap-12 p-12 rounded-xl"
      role="region"
      aria-label="Message feedback">
      <div
        className="absolute inset-0 rounded-xl border border-moon-250 bg-white dark:bg-moon-850 dark:border-moon-650"
        style={{filter: nightModeRevertFilter}}
      />
      <div className="relative flex items-start self-stretch justify-between">
        <span className="text-lg font-semibold text-moon-800">
          What went wrong?
        </span>
        <IconButton
          icon="close"
          size="sm"
          aria-label="close feedback box"
          variant="ghost"
          onClick={() => onDismiss()}
        />
      </div>
      <div className="relative flex items-start content-start flex-wrap gap-8">
        {suggestions.map(suggestion => (
          <ToggleButton
            key={suggestion}
            variant="secondary"
            isSelected={selectedSuggestions.has(suggestion)}
            onChange={isSelected =>
              handleToggleSuggestion(suggestion, isSelected)
            }>
            {suggestion}
          </ToggleButton>
        ))}
      </div>
      <div className="relative">
        <TextField
          aria-label="feedback details"
          fullWidth={true}
          placeholder="Share details (optional)"
          value={details}
          onChange={setDetails}
        />
      </div>
      <div className="relative flex justify-end">
        <Button
          variant="primary"
          onPress={handleSubmit}
          isDisabled={details.trim() === '' && selectedSuggestions.size === 0}>
          Submit
        </Button>
      </div>
    </div>
  );
};

const UserText: FC = () => {
  const {text} = useMessagePartText();
  const cleaned = decodePromptForMessageDisplay(text);
  return (
    <div
      className="[&_p:last-child]:mb-0" /* MarkdownContainer styles with mb-[16px]. This overrides that*/
    >
      <WBAgentMarkdown content={cleaned} />
    </div>
  );
};

export const UserMessage: FC = () => {
  const nightModeRevertFilter = useNightModeRevertFilter();

  return (
    <MessagePrimitive.Root
      className="flex flex-col items-end gap-10 pl-24 pt-16"
      data-role="user">
      <div className="relative max-w-full break-words overflow-hidden rounded-2xl px-16 py-8 text-moon-800 text-[15px] font-normal">
        <div
          className="absolute inset-0 rounded-2xl bg-moon-100 dark:bg-moon-750"
          style={{filter: nightModeRevertFilter}}
        />
        <div className="relative">
          <MessagePrimitive.Parts components={{Text: UserText}} />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};
